import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@resideo/blueprint-react';
import { AccountType } from 'types/types';
import AccountTypeIcon from './AccountTypeIcon';

export interface AccountTypeButtonProps {
  accountType: AccountType;
  active?: boolean;
  onChange: (state?: boolean, accountType?: AccountType) => any;
  className?: string;
}

const Container = styled(Flex)`
  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    flex-direction: row;
  }
`;

const Button = styled.button<{ active: boolean }>`
  cursor: pointer;
  width: 262px;
  height: 213px;
  box-sizing: border-box;
  padding: 15px 10px;
  text-align: left;
  background: #ffffff;
  color: #153755;
  border: 3px solid #cccccc;
  border-radius: 10px;
  transition: all 0.1s ease-out;
  ${({ active }) =>
    active &&
    `
      box-shadow: 0px 3px 10px #EFF5FF;
      outline: 3px solid #91B9FF;
      border: 2px solid #91B9FF;
    `}

  &:hover {
    background: #dbebff;
  }

  &:active {
    background: #bfdcff;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.large}) {
    height: 143px;
  }
`;

const AccountTypeButton: FC<AccountTypeButtonProps> = ({
  accountType,
  active,
  onChange,
  className,
}): JSX.Element => {
  const { t } = useTranslation();

  const accountTypeText = {
    pro: {
      title: t('getStarted.accountTypes.proTitle'),
      desc: t('getStarted.accountTypes.proDesc'),
    },
    distributor: {
      title: t('getStarted.accountTypes.distributorTitle'),
      desc: t('getStarted.accountTypes.distributorDesc'),
    },
    developer: {
      title: t('getStarted.accountTypes.developerTitle'),
      desc: t('getStarted.accountTypes.developerDesc'),
    },
    personal: {
      title: t('getStarted.accountTypes.personalTitle'),
      desc: t('getStarted.accountTypes.personalDesc'),
    },
  };

  return (
    <Button
      className={className}
      active={!!active}
      data-test-select-account-type-btn
      onClick={() => {
        onChange(!active, accountType);
      }}>
      <Container flexDirection='column'>
        <Flex
          justifyContent='center'
          paddingTop='small'
          marginBottom='small'
          marginRight='small'>
          <AccountTypeIcon accountType={accountType} />
        </Flex>
        <Flex flexDirection='column' height='90px'>
          <Text
            fontSize='medium'
            fontWeight='bold'
            marginBottom='small'
            data-test-select-account-type-title>
            {accountTypeText[accountType].title}
          </Text>
          <Text fontSize='medium' data-test-select-account-type-desc>
            {accountTypeText[accountType].desc}
          </Text>
        </Flex>
      </Container>
    </Button>
  );
};

export default AccountTypeButton;
